/**
 * CardTile Component
 * Reusable card display — shows card art when available, else text layout.
 * Also supports optional price overlay for commerce views.
 */

import { DomainBadges } from './DomainIcon.jsx';
import { getCardImageUrl, getCardDomainsForDisplay, isBattlefieldCard } from './cardUtils.js';
import { getRarityIconUrl } from './rarityIcons.js';
import CardTextWithIcons from './CardTextWithIcons.jsx';
import { MIGHT_ICON_URL, POWER_ICON_URL } from './statIcons.js';
import { isBasicRune, getDisplayPriceValue } from './filterOptions.js';
import {
  formatPriceValue,
  computeRawPrices,
  periodLabel as getPeriodLabel
} from './priceUtils.js';

export default function CardTile({
  card,
  onClick,
  onRightClick,
  treatment = null,
  quantity = 0,
  foilQuantity = 0,
  isIllegal = false,
  dimmed = false,
  className = '',
   imageWrapClassName = '',
  // Pricing-related props
  showPrice = false,
  pricePeriod = '7'
}) {
  if (!card) {
    return (
      <div className="card-tile card-tile-empty">
        <div className="card-tile-placeholder">No card</div>
      </div>
    );
  }

  const handleClick = (e) => {
    e.preventDefault();
    if (onClick) onClick(card);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    if (onRightClick) onRightClick(card);
  };

  const imageUrl = getCardImageUrl(card);
  const cardType = card.cardType?.type?.[0]?.label || card.cardType?.type?.[0]?.id || 'Unknown';
  const superType = card.cardType?.superType?.[0]?.label || '';
  const rarity = card.rarity?.value?.label || card.rarity || 'Common';
  const domains = getCardDomainsForDisplay(card);
  const energy = card.energy?.value?.id ?? card.energy ?? null;
  const power = card.power?.value?.id ?? card.power ?? null;
  const might = card.might?.value?.id ?? card.might ?? null;
  const rarityClass = `rarity-${String(rarity).toLowerCase().replace(/\s/g, '-')}`;

  const isBattlefield = isBattlefieldCard(card);
  const treatmentClass = treatment ? `treatment-${treatment}` : '';
  const illegalClass = isIllegal ? 'card-illegal' : '';
  const dimmedClass = dimmed ? 'card-dimmed' : '';
  const battlefieldClass = isBattlefield ? 'battlefield' : '';
  const isPromo = !!card.promo;
  const rarityIconUrl = getRarityIconUrl(card);

  // Price overlay computation (optional)
  let priceLabel = null;
  let foilPriceLabel = null;
  if (showPrice && card.price) {
    const { rawNormal, rawFoil } = computeRawPrices(card, rarity);
    const basePriceValue = getDisplayPriceValue(
      { ...card.price, normal: rawNormal, foil: rawFoil },
      rarity,
      cardType,
      isPromo
    );
    const display = formatPriceValue(basePriceValue);
    if (display) {
      priceLabel = `${display} · ${getPeriodLabel(pricePeriod)}`;
    }

    // Optional separate foil price when available AND we have a normal price.
    const hasNormal = rawNormal != null;
    const foilDisplay = formatPriceValue(rawFoil);
    if (hasNormal && foilDisplay && !isBasicRune(rarity, cardType)) {
      foilPriceLabel = foilDisplay;
    }
  }

  return (
    <div
      className={`card-tile ${battlefieldClass} ${treatmentClass} ${illegalClass} ${dimmedClass} ${className}`}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      title={`${card.name} - ${cardType}`}
    >
      {imageUrl ? (
        <div className="card-tile-frame">
          <div className="card-tile-header-bar">
            <div className="card-tile-header-row">
              <div className="card-tile-header-main" title={card.name}>
                {card.name}
              </div>
              {isPromo && (
                <span className="card-tile-promo-icon" title="Promo card">
                  ★
                </span>
              )}
            </div>
            <div className="card-tile-header-meta">
              {card.id && (
                <span className="card-tile-header-id">{card.id}</span>
              )}
              {priceLabel && (
                <>
                  <span className="card-tile-header-separator">|</span>
                  <span className={`card-tile-header-price ${foilPriceLabel ? 'card-tile-header-price--multi' : 'card-tile-header-price--single'}`}>
                    {priceLabel}
                    {foilPriceLabel && (
                      <span className="card-tile-header-price-foil">
                        {' '}
                        / {foilPriceLabel}
                      </span>
                    )}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="card-tile-image-wrap">
            {isBattlefield && imageWrapClassName ? (
              <div className={imageWrapClassName}>
                <img
                  src={imageUrl}
                  alt={card.name || ''}
                  className="card-tile-image"
                  loading="lazy"
                />
              </div>
            ) : (
              <img
                src={imageUrl}
                alt={card.name || ''}
                className="card-tile-image"
                loading="lazy"
              />
            )}
          </div>
        </div>
      ) : (
        <div className="card-tile-container">
          <div className="card-header">
            <div className="card-name-section">
              <div className="card-name">{card.name}</div>
              {superType && <div className="card-supertype">{superType}</div>}
            </div>
            {energy !== null && <div className="card-energy-cost">{energy}</div>}
          </div>
          <div className="card-type-line">
            <span className="card-type-text">
              <strong>{cardType}</strong>
            </span>
            {domains.length > 0 && (
              <>
                <span className="card-type-sep">—</span>
                <span className="card-domains-inline">
                  <DomainBadges domains={domains} />
                </span>
              </>
            )}
          </div>
          <div className="card-text-box">
            <div className="card-text card-text-with-icons">
              <CardTextWithIcons card={card} size="small" />
            </div>
          </div>
          {(power !== null || might !== null) && (
            <div className="card-stats-row">
              {power !== null && (
                <span className="card-stat">
                  <img src={POWER_ICON_URL} alt="" className="card-stat-icon" aria-hidden />
                  <span>{power}</span>
                </span>
              )}
              {might !== null && (
                <span className="card-stat">
                  <img src={MIGHT_ICON_URL} alt="" className="card-stat-icon" aria-hidden />
                  <span>{might}</span>
                </span>
              )}
            </div>
          )}
          <div className="card-footer">
            <div className="card-set-info">
              <span className="card-id">{card.id}</span>
              <span className={`card-rarity ${rarityClass}`}>{rarity}</span>
            </div>
          </div>
        </div>
      )}

      {treatment && (
        <div className={`treatment-indicator treatment-${treatment}`}>
          {treatment === 'foil' && rarityIconUrl && (
            <img src={rarityIconUrl} alt="Foil" className="card-tile-rarity-icon" />
          )}
          {treatment !== 'foil' && rarityIconUrl && (
            <img src={rarityIconUrl} alt={rarity} className="card-tile-rarity-icon" />
          )}
        </div>
      )}

      {(quantity > 0 || foilQuantity > 0) && (
        <div className="card-quantity">
          {quantity > 0 && <span className="qty-normal">×{quantity}</span>}
          {foilQuantity > 0 && <span className="qty-foil">✨×{foilQuantity}</span>}
        </div>
      )}
    </div>
  );
}
